// Lightweight performance instrumentation. Reports core web vitals + custom
// route-render marks to console in dev, POSTs to /api/log/perf in production.
//
// Production logs land in App Engine Cloud Logging via server's request handler.
// Filter Cloud Logging by jsonPayload.kind="client_perf" to query.

type ClientPerfEntry = {
  kind: "client_perf";
  metric: string;
  value: number;
  rating?: "good" | "needs-improvement" | "poor";
  path: string;
  ts: number;
};

const isProd = import.meta.env.PROD;
const queue: ClientPerfEntry[] = [];
let flushTimer: number | null = null;

function flush() {
  if (flushTimer !== null) {
    window.clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (queue.length === 0) return;
  const batch = queue.splice(0);
  if (!isProd) {
    for (const entry of batch) {
      // eslint-disable-next-line no-console
      console.log("[perf]", entry.metric, Math.round(entry.value), entry.rating ?? "");
    }
    return;
  }
  // Best-effort POST. Use sendBeacon on unload for reliability.
  try {
    const body = JSON.stringify({ entries: batch });
    if (navigator.sendBeacon) {
      navigator.sendBeacon("/api/log/perf", new Blob([body], { type: "application/json" }));
    } else {
      void fetch("/api/log/perf", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body,
        keepalive: true,
      });
    }
  } catch {
    /* swallow */
  }
}

function record(metric: string, value: number, rating?: ClientPerfEntry["rating"]) {
  queue.push({
    kind: "client_perf",
    metric,
    value,
    rating,
    path: typeof window !== "undefined" ? window.location.pathname : "",
    ts: Date.now(),
  });
  if (flushTimer === null) {
    flushTimer = window.setTimeout(flush, 5000);
  }
}

function rateLcp(ms: number): ClientPerfEntry["rating"] {
  if (ms <= 2500) return "good";
  if (ms <= 4000) return "needs-improvement";
  return "poor";
}

function rateInp(ms: number): ClientPerfEntry["rating"] {
  if (ms <= 200) return "good";
  if (ms <= 500) return "needs-improvement";
  return "poor";
}

function rateCls(value: number): ClientPerfEntry["rating"] {
  if (value <= 0.1) return "good";
  if (value <= 0.25) return "needs-improvement";
  return "poor";
}

export function initClientPerf() {
  if (typeof window === "undefined" || typeof PerformanceObserver === "undefined") return;

  // Largest Contentful Paint — perceived load speed.
  try {
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const last = entries[entries.length - 1] as PerformanceEntry & { startTime: number };
      if (last) record("lcp", last.startTime, rateLcp(last.startTime));
    });
    lcpObserver.observe({ type: "largest-contentful-paint", buffered: true });
  } catch {
    /* unsupported */
  }

  // Cumulative Layout Shift — visual stability.
  try {
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as Array<PerformanceEntry & { value: number; hadRecentInput: boolean }>) {
        if (!entry.hadRecentInput) clsValue += entry.value;
      }
    });
    clsObserver.observe({ type: "layout-shift", buffered: true });
    window.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        record("cls", clsValue, rateCls(clsValue));
      }
    });
  } catch {
    /* unsupported */
  }

  // Interaction to Next Paint — responsiveness on tap/click.
  try {
    const inpObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as Array<PerformanceEntry & { duration: number }>) {
        if (entry.duration > 40) record("inp", entry.duration, rateInp(entry.duration));
      }
    });
    inpObserver.observe({ type: "event", buffered: true, durationThreshold: 40 } as PerformanceObserverInit);
  } catch {
    /* unsupported */
  }

  // Time to first byte — server response speed including TLS / cold start.
  try {
    const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
    if (nav) record("ttfb", nav.responseStart - nav.requestStart);
  } catch {
    /* unsupported */
  }

  // Flush queued entries before unload so we don't lose the final batch.
  window.addEventListener("pagehide", flush);
  window.addEventListener("beforeunload", flush);
}

// Mark a custom flow timing — useful for "ProductDetail mount → first paint",
// "Cart open → drawer animation done", etc.
//
// Usage:
//   const stop = markFlow("home_feed_mount");
//   // ... after render ...
//   stop();
export function markFlow(label: string): () => void {
  const start = performance.now();
  return () => {
    record(`flow_${label}`, performance.now() - start);
  };
}
